import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@/api/generated';
import { useLanguage } from '@/contexts/language-context';
import type { LoginFormData } from '@/form-schemas/auth-schemas';
import { loginSchema, loginFormDefaults } from '@/form-schemas/auth-schemas';
import type { MfaFlowApiResponse } from '@/hooks/use-mfa-orchestrator.types';
import { startMfaFlow } from '@/services/apis/mfa-orchestrator.api';

const LOGIN_REFUSED_ERROR_TYPE = 'user_cannot_login';

function isLoginRefused(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const body = error.body as { detail?: { type?: string } } | undefined;

  return body?.detail?.type === LOGIN_REFUSED_ERROR_TYPE;
}

interface UseLoginFormProps {
  authId: string;
  onSuccessLogin: (result: MfaFlowApiResponse, username: string) => void;
  initialUsername?: string;
}

export function useLoginForm({
  authId,
  onSuccessLogin,
  initialUsername,
}: UseLoginFormProps) {
  const { t } = useLanguage();

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      ...loginFormDefaults,
      ...(initialUsername ? { username: initialUsername } : {}),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { formState, setError, clearErrors } = form;
  const { isSubmitting, errors } = formState;

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearErrors();
      const response = await startMfaFlow(data.username, authId);
      onSuccessLogin(response, data.username);
    } catch (error) {
      setError('root', {
        type: 'manual',
        message: isLoginRefused(error)
          ? t('auth.cannotLogInAsUser')
          : t('auth.loginError'),
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      form.handleSubmit(onSubmit)();
    }
  };

  const getErrorMessage = () =>
    errors.username?.message ?? errors.root?.message ?? '';

  return {
    form,
    onSubmit,
    handleKeyPress,
    isSubmitting,
    getErrorMessage,
  };
}
